import { MessageSquareText } from 'lucide-react';
import { useEffect } from 'react';

import { CareCoordinatorFilter } from '@apps/web/components/filters/care_coordinator_filter';
import { PendingMessageCard } from '@apps/web/components/shared/messages/message_pending_card';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { useOrganizationPendingMessagesRealtime } from '@apps/web/hooks/realtime_hooks';
import { useCareCoordinatorFilter } from '@apps/web/hooks/use_care_coordinator_filter';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type StagedMessageDomain } from '@libraries/typescript/api_client';

type PendingMessagesDialogProps = {
    readonly organizationId: string;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly domain?: StagedMessageDomain;
    readonly domainId?: string | null;
    readonly careCoordinatorIds?: Array<string>;
    /** Hide the care coordinator filter (e.g., when viewing a single outreach) */
    readonly showCareCoordinatorFilter?: boolean;
};

export function PendingMessagesDialog({
    organizationId,
    open,
    onOpenChange,
    domain,
    domainId,
    careCoordinatorIds: initialCareCoordinatorIds,
    showCareCoordinatorFilter = true,
}: PendingMessagesDialogProps) {
    const { formatName } = useFormatPersonName();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'messages-pending-dialog',
    });

    const careCoordinatorSupportEnabled =
        !!organization?.features?.care_coordinator_support_enabled;

    // Care coordinator filtering only applies to shift_confirmation domain.
    // Other domains (like callout) don't have a client relationship needed for filtering.
    const shouldApplyCareCoordinatorFilter =
        careCoordinatorSupportEnabled &&
        (domain === undefined || domain === 'shift_confirmation');

    // Use the shared filter hook, initialized with the passed-in IDs (from parent like shift-list-pane)
    const careCoordinatorFilter = useCareCoordinatorFilter({
        enabled: shouldApplyCareCoordinatorFilter,
        initialSelectedIds: initialCareCoordinatorIds,
    });

    // Use the filter's selected IDs for the query
    const effectiveCareCoordinatorIds = shouldApplyCareCoordinatorFilter
        ? careCoordinatorFilter.filterIds
        : undefined;
    const includeUnassignedCareCoordinators = shouldApplyCareCoordinatorFilter
        ? careCoordinatorFilter.includeUnassigned
        : undefined;

    const { data, isLoading, mutate } = useOrganizationPendingMessagesRealtime(
        'messages-pending-dialog',
        !open,
        {
            autoUpdate: open,
            ...(domain && { domain }),
            ...(domainId && { domainId }),
            ...(effectiveCareCoordinatorIds && {
                careCoordinatorIds: effectiveCareCoordinatorIds,
            }),
            ...(includeUnassignedCareCoordinators && {
                includeUnassignedCareCoordinators,
            }),
        },
    );

    // Reset filter to parent's selection when dialog opens
    const { resetToInitial } = careCoordinatorFilter;
    useEffect(() => {
        if (open) {
            resetToInitial();
        }
    }, [open, resetToInitial]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="top-[10%] max-h-[85vh] max-w-7xl min-w-[1200px] translate-y-0">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center gap-2">
                            <MessageSquareText className="h-5 w-5" />
                            Responses to Approve
                            {data && data.length > 0 && (
                                <Badge variant="secondary">{data.length}</Badge>
                            )}
                        </DialogTitle>
                        {showCareCoordinatorFilter &&
                            shouldApplyCareCoordinatorFilter &&
                            careCoordinatorFilter.careCoordinators &&
                            careCoordinatorFilter.careCoordinators.length >
                                0 && (
                                <CareCoordinatorFilter
                                    careCoordinators={
                                        careCoordinatorFilter.careCoordinators
                                    }
                                    triggerClassName="mr-4"
                                    isOpen={careCoordinatorFilter.isOpen}
                                    onOpenChange={
                                        careCoordinatorFilter.setIsOpen
                                    }
                                    searchValue={
                                        careCoordinatorFilter.searchValue
                                    }
                                    onSearchValueChange={
                                        careCoordinatorFilter.setSearchValue
                                    }
                                    displayValue={
                                        careCoordinatorFilter.displayValue
                                    }
                                    onSelectCoordinator={
                                        careCoordinatorFilter.handleSelect
                                    }
                                    onShowAll={
                                        careCoordinatorFilter.handleShowAll
                                    }
                                    isCoordinatorSelected={
                                        careCoordinatorFilter.isCoordinatorSelected
                                    }
                                    isShowingAll={
                                        careCoordinatorFilter.isShowingAll
                                    }
                                    formatCoordinatorName={
                                        careCoordinatorFilter.formatCoordinatorName
                                    }
                                />
                            )}
                    </div>
                    <DialogDescription>
                        Review and approve AI-generated responses to caregiver
                        messages
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="max-h-[calc(85vh-120px)] pr-4">
                    {isLoading ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground text-sm">
                                Loading pending messages…
                            </div>
                        </div>
                    ) : !data || data.length === 0 ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground text-sm">
                                No pending messages to approve
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {data.map((pm) => (
                                <PendingMessageCard
                                    key={pm.id}
                                    pendingMessage={pm}
                                    organizationId={organizationId!}
                                    timezone={organization?.timezone ?? 'UTC'}
                                    title={
                                        formatName(pm.caregiver_name) ||
                                        'Caregiver'
                                    }
                                    titleTo="/conversations"
                                    titleSearch={{
                                        conversationId: pm.conversation_id,
                                    }}
                                    onUpdate={mutate}
                                />
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}
